import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMyTariff } from '../api/subscription';
import { getTariffs } from '../api/getTariffs';
import { getUserAdd } from '../api/getuserad';

// Used only before /subscription/current resolves or for guests — the backend
// returns full `conditions` even for users without a subscription.
const DEFAULT_CONDITIONS = {
    max_active_announcements: 3,
    max_photos_per_ad: 5,
    vip_hours: 0,
    analytics_enabled: false,
    personal_manager: false,
    banner_included: false,
    tiktok_videos: 0,
    negotiable_price: false,
};

const DEFAULT_USAGE = {
    active_announcements: 0,
    unlimited_announcements: false,
    remaining_announcements: 0,
    announcements_limit_reached: false,
    vip_hours_balance: 0,
};

const TariffContext = createContext({
    plan: null,
    activeTariffId: null,
    conditions: DEFAULT_CONDITIONS,
    usage: DEFAULT_USAGE,
    isActive: false,
    isLoading: true,
    refresh: () => {},
});

export function TariffProvider({ children }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const refresh = useCallback(async () => {
        const fresh = await getMyTariff();
        setData(fresh);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // TariffProvider app түбірінде бір рет орныған, login/logout кезінде қайта
    // mount болмайды. Сондықтан аккаунт ауысқанда мұнда жаңартып отырамыз:
    // кіргенде — жаңа пайдаланушының подпискасын қайта жүктейміз, ал шыққанда —
    // күйді тазалаймыз, әйтпесе алдыңғы аккаунттың тарифі басқа аккаунтқа өтеді.
    useEffect(() => {
        const onLogin = () => { refresh(); };
        const onLogout = () => { setData(null); setIsLoading(false); };
        window.addEventListener('userLogin', onLogin);
        window.addEventListener('userLogout', onLogout);
        return () => {
            window.removeEventListener('userLogin', onLogin);
            window.removeEventListener('userLogout', onLogout);
        };
    }, [refresh]);

    // Warm caches for the profile #ads tab on app load so it renders
    // instantly when opened: public tariffs list + the default active ads list.
    useEffect(() => {
        getTariffs().catch(() => {});
        getUserAdd('active', 1, 10).catch(() => {});
    }, []);

    const conditions = data?.conditions ?? data?.plan?.conditions ?? DEFAULT_CONDITIONS;
    const usage = data?.usage ?? DEFAULT_USAGE;
    const isActive = Boolean(data?.is_active);
    const effectiveCommissionRate = data?.effective_commission_rate ?? data?.plan?.commission_rate ?? null;
    // Resolve the active tariff id from any of the shapes the backend may return:
    // nested plan object, flat top-level field, or nested tariff object.
    const activeTariffId = data?.plan?.id ?? data?.plan?.tariff_id ?? data?.tariff_id ?? data?.plan_id ?? data?.tariff?.id ?? null;
    const vipHoursBalance = Number(data?.vip_hours?.balance ?? usage.vip_hours_balance ?? 0);

    return (
        <TariffContext.Provider
            value={{
                plan: data?.plan ?? null,
                activeTariffId,
                conditions,
                usage,
                isActive,
                isLoading,
                refresh,
                startedAt: data?.started_at ?? null,
                expiresAt: data?.expires_at ?? null,
                effectiveCommissionRate,
                vipHoursBalance,
            }}
        >
            {children}
        </TariffContext.Provider>
    );
}

export function useMyTariff() {
    return useContext(TariffContext);
}